import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export interface SettingDropdownOption {
  value: string;
  label: string;
}

interface Props {
  /** Panel title (e.g. "Model", "Trace level"). */
  title: string;
  /** The chat command shown inside the panel header (e.g. "/model"). Optional —
   *  omit for controls with no chat command (e.g. language). */
  command?: string;
  /** Currently selected value. */
  value: string;
  options: SettingDropdownOption[];
  onChange: (value: string) => void;
}

/**
 * A compact custom dropdown for the btgpt header settings (model / trace),
 * replacing a native <select> so the matching chat command can live INSIDE the
 * open panel — mirroring the /permissions hint in the permissions popover. The
 * trigger shows the current value; the panel lists the options + the command.
 */
export const SettingDropdown: React.FC<Props> = ({ title, command, value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="h-8 inline-flex items-center gap-1 rounded-full border border-ai-border bg-ai-surface px-3 text-xs font-medium text-ai-text hover:border-ai-border-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-ai-focus-ring cursor-pointer"
          data-testid="btgpt-setting-dropdown"
        >
          <span>{current?.label ?? (value || 'Auto')}</span>
          <ChevronDown className="w-3.5 h-3.5 text-ai-text-faint" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-48 p-2 rounded-ai-md"
        style={{ backgroundColor: 'var(--ai-surface)', color: 'var(--ai-text)', borderColor: 'var(--ai-border-strong)' }}
      >
        <div className="flex items-center justify-between px-1 pb-1.5 mb-1 border-b border-ai-border">
          <span className="text-xs font-semibold text-ai-text">{title}</span>
          {command ? <span className="text-[10px] text-ai-text-faint font-mono select-text cursor-text">{command}</span> : null}
        </div>
        <div className="flex flex-col">
          {options.map((o) => {
            const active = o.value === value;
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => { onChange(o.value); setOpen(false); }}
                className={[
                  'text-left px-2 py-1.5 rounded text-xs transition-colors cursor-pointer',
                  active
                    ? 'bg-ai-accent-soft text-ai-text font-medium'
                    : 'text-ai-text hover:bg-ai-muted',
                ].join(' ')}
              >
                {o.label}{active ? ' ✓' : ''}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SettingDropdown;
